import React, { useState } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { format, parseISO, isBefore, addMonths } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, AlertTriangle, Clock, DollarSign, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export default function ParcelasFinanciamento({ financiamento, parcelas, onUpdate }) {
  const [pagando, setPagando] = useState(null);
  const [settleDialogOpen, setSettleDialogOpen] = useState(false);
  const [settleParcela, setSettleParcela] = useState(null);
  const [settleData, setSettleData] = useState({ juros: "0", multa: "0", desconto: "0", data_pagamento: new Date().toISOString().split('T')[0], observacao: "" });
  const [selectedIds, setSelectedIds] = useState([]);
  const [renegDialogOpen, setRenegDialogOpen] = useState(false);
  const [renegData, setRenegData] = useState({ taxa_juros_percent: "0", desconto_total: "0", juros_extra_total: "0", novas_parcelas: "1", primeiro_vencimento: new Date().toISOString().split('T')[0] });
  const [renegConfirm, setRenegConfirm] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundParcela, setRefundParcela] = useState(null);
  const [refundMotivo, setRefundMotivo] = useState("");
  const [refundConfirm, setRefundConfirm] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const abrirPagamento = (parcela) => {
    setSettleParcela(parcela);
    setSettleData({ juros: "0", multa: "0", desconto: "0", data_pagamento: new Date().toISOString().split('T')[0], observacao: "" });
    setSettleDialogOpen(true);
  };

  const confirmarPagamento = async () => {
    if (!settleParcela) return;
    setPagando(settleParcela.id);
    const juros = parseFloat(settleData.juros) || 0;
    const multa = parseFloat(settleData.multa) || 0;
    const desconto = parseFloat(settleData.desconto) || 0;
    const totalPago = (settleParcela.valor_total || 0) + juros + multa - desconto;
    const dataPag = settleData.data_pagamento || new Date().toISOString().split('T')[0];

    try {
      await agrogestao.entities.ParcelaFinanciamento.update(settleParcela.id, {
        status: 'pago',
        data_pagamento: dataPag,
        valor_pago: totalPago,
        multa_pago: multa,
        desconto_concedido: desconto
      });

      await agrogestao.entities.Lancamento.create({
        tipo: 'despesa',
        categoria: 'Financiamento (Parcela)',
        descricao: settleData.observacao 
          ? `Parcela ${settleParcela.numero_parcela}/${financiamento.total_parcelas} • ${financiamento.numero_contrato || financiamento.instituicao || ''} • ${settleData.observacao}`
          : `Parcela ${settleParcela.numero_parcela}/${financiamento.total_parcelas} • ${financiamento.numero_contrato || financiamento.instituicao || ''}`,
        valor: totalPago,
        data: dataPag,
        propriedade_id: settleParcela.propriedade_id,
        forma_pagamento: 'financiamento',
        status: 'pago',
        fornecedor_cliente: financiamento.instituicao || ''
      });

      const parcelasPagas = (financiamento.parcelas_pagas || 0) + 1;
      const novoSaldo = (financiamento.saldo_devedor || financiamento.valor_total) - (settleParcela.valor_principal || 0);
      await agrogestao.entities.Financiamento.update(financiamento.id, {
        parcelas_pagas: parcelasPagas,
        saldo_devedor: Math.max(0, novoSaldo),
        status: parcelasPagas >= financiamento.total_parcelas ? 'quitado' : 'ativo'
      });

      setSettleDialogOpen(false);
      setSettleParcela(null);
      onUpdate();
    } finally {
      setPagando(null);
    }
  };

  const parcelasOrdenadas = [...parcelas].sort((a, b) => a.numero_parcela - b.numero_parcela);
  const hoje = new Date();

  const totais = {
    pago: parcelas.filter(p => p.status === 'pago').reduce((a, p) => a + (p.valor_pago || p.valor_total || 0), 0),
    pendente: parcelas.filter(p => p.status !== 'pago').reduce((a, p) => a + (p.valor_total || 0), 0),
    atrasado: parcelas.filter(p => p.status !== 'pago' && isBefore(parseISO(p.data_vencimento), hoje)).reduce((a, p) => a + (p.valor_total || 0), 0)
  };

  return (
    <div className="space-y-4">
      {/* Resumo */}
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 bg-green-50 rounded-lg text-center">
          <p className="text-xs text-green-600">Pago</p>
          <p className="font-bold text-green-700">{formatCurrency(totais.pago)}</p>
        </div>
        <div className="p-3 bg-blue-50 rounded-lg text-center">
          <p className="text-xs text-blue-600">Pendente</p>
          <p className="font-bold text-blue-700">{formatCurrency(totais.pendente)}</p>
        </div>
        <div className="p-3 bg-red-50 rounded-lg text-center">
          <p className="text-xs text-red-600">Atrasado</p>
          <p className="font-bold text-red-700">{formatCurrency(totais.atrasado)}</p>
        </div>
      </div>

      {/* Lista de Parcelas */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-100">
            <tr>
              <th className="text-left py-2 px-3 font-semibold">#</th>
              <th className="text-left py-2 px-3 font-semibold">Vencimento</th>
              <th className="text-right py-2 px-3 font-semibold">Valor</th>
              <th className="text-right py-2 px-3 font-semibold">Juros</th>
              <th className="text-right py-2 px-3 font-semibold">Multa</th>
              <th className="text-right py-2 px-3 font-semibold">Desconto</th>
              <th className="text-right py-2 px-3 font-semibold">Original</th>
              <th className="text-right py-2 px-3 font-semibold">Pago</th>
              <th className="text-right py-2 px-3 font-semibold">Ajustes</th>
              <th className="text-center py-2 px-3 font-semibold">Status</th>
              <th className="text-right py-2 px-3 font-semibold">Ação</th>
            </tr>
          </thead>
          <tbody>
            {parcelasOrdenadas.map(parcela => {
              const vencida = parcela.status !== 'pago' && isBefore(parseISO(parcela.data_vencimento), hoje);
              return (
                <tr 
                  key={parcela.id} 
                  className={`border-t ${vencida ? 'bg-red-50' : parcela.status === 'pago' ? 'bg-green-50' : ''}`}
                >
                  <td className="py-2 px-3 font-medium">
                    {parcela.status !== 'pago' && (
                      <input type="checkbox" className="mr-2" checked={selectedIds.includes(parcela.id)} onChange={(e) => {
                        setSelectedIds((prev) => e.target.checked ? [...prev, parcela.id] : prev.filter(id => id !== parcela.id));
                      }} />
                    )}
                    {parcela.numero_parcela}
                  </td>
                  <td className="py-2 px-3">
                    {format(parseISO(parcela.data_vencimento), "dd/MM/yyyy")}
                    {parcela.data_pagamento && (
                      <span className="text-xs text-green-600 block">
                        Pago em {format(parseISO(parcela.data_pagamento), "dd/MM/yyyy")}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right font-medium">
                    {formatCurrency(parcela.valor_total)}
                  </td>
                  <td className="py-2 px-3 text-right">{formatCurrency(parcela.valor_juros || 0)}</td>
                  <td className="py-2 px-3 text-right">{formatCurrency(parcela.multa_pago || 0)}</td>
                  <td className="py-2 px-3 text-right">{formatCurrency(parcela.desconto_concedido || 0)}</td>
                  <td className="py-2 px-3 text-right">{formatCurrency((parcela.valor_principal || 0))}</td>
                  <td className="py-2 px-3 text-right">{formatCurrency(parcela.valor_pago || 0)}</td>
                  <td className="py-2 px-3 text-right">{formatCurrency((parcela.valor_pago || 0) - (parcela.valor_total || 0))}</td>
                  <td className="py-2 px-3 text-center">
                    {parcela.status === 'pago' ? (
                      <Badge className="bg-green-100 text-green-700 gap-1">
                        <Check className="w-3 h-3" /> Pago
                      </Badge>
                    ) : vencida ? (
                      <Badge className="bg-red-100 text-red-700 gap-1">
                        <AlertTriangle className="w-3 h-3" /> Atrasada
                      </Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-700 gap-1">
                        <Clock className="w-3 h-3" /> Pendente
                      </Badge>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {parcela.status !== 'pago' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => abrirPagamento(parcela)}
                        disabled={pagando === parcela.id}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      >
                        {pagando === parcela.id ? (
                          <span className="animate-pulse">...</span>
                        ) : (
                          <>
                            <DollarSign className="w-3 h-3 mr-1" />
                            Pagar
                          </>
                        )}
                      </Button>
                    )}
                    {parcela.status === 'pago' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setRefundParcela(parcela); setRefundMotivo(""); setRefundConfirm(false); setRefundDialogOpen(true); }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                      >
                        Estornar
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3">
        <div className="text-xs text-stone-500">Selecionadas: {selectedIds.length}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" disabled={selectedIds.length === 0} onClick={() => { setRenegDialogOpen(true); setRenegConfirm(false); }}>
            Renegociar selecionadas
          </Button>
        </div>
      </div>
      <Dialog open={settleDialogOpen} onOpenChange={(v) => { setSettleDialogOpen(v); if (!v) setSettleParcela(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Quitar Parcela</DialogTitle>
            <DialogDescription>Informe ajustes e confirme o pagamento</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Juros (R$)</Label>
              <Input type="number" step="0.01" value={settleData.juros} onChange={(e) => setSettleData({ ...settleData, juros: e.target.value })} />
            </div>
            <div>
              <Label>Multa (R$)</Label>
              <Input type="number" step="0.01" value={settleData.multa} onChange={(e) => setSettleData({ ...settleData, multa: e.target.value })} />
            </div>
            <div>
              <Label>Desconto (R$)</Label>
              <Input type="number" step="0.01" value={settleData.desconto} onChange={(e) => setSettleData({ ...settleData, desconto: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Data do Pagamento</Label>
              <Input type="date" value={settleData.data_pagamento} onChange={(e) => setSettleData({ ...settleData, data_pagamento: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Observação</Label>
              <Textarea rows={2} placeholder="Observações da quitação" value={settleData.observacao} onChange={(e) => setSettleData({ ...settleData, observacao: e.target.value })} />
            </div>
          </div>
          <div className="mt-4 p-3 bg-stone-50 rounded-xl border border-stone-200">
            <div className="flex items-center justify-between">
              <p className="text-sm text-stone-600">Valor original</p>
              <p className="font-semibold text-stone-800">{formatCurrency(settleParcela?.valor_total || 0)}</p>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-sm text-stone-600">Total a pagar</p>
              <p className="text-lg font-bold text-green-700">
                {formatCurrency((settleParcela?.valor_total || 0) + (parseFloat(settleData.juros) || 0) + (parseFloat(settleData.multa) || 0) - (parseFloat(settleData.desconto) || 0))}
              </p>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setSettleDialogOpen(false); setSettleParcela(null); }} className="flex-1">Cancelar</Button>
            <Button onClick={confirmarPagamento} className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]" disabled={pagando === settleParcela?.id}>
              {pagando === settleParcela?.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={renegDialogOpen} onOpenChange={setRenegDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Renegociação de Parcelas</DialogTitle>
            <DialogDescription>Configure novos termos e gere um novo cronograma</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Taxa de juros (%) a.a.</Label>
              <Input type="number" step="0.01" value={renegData.taxa_juros_percent} onChange={(e) => setRenegData({ ...renegData, taxa_juros_percent: e.target.value })} />
            </div>
            <div>
              <Label>Desconto total (R$)</Label>
              <Input type="number" step="0.01" value={renegData.desconto_total} onChange={(e) => setRenegData({ ...renegData, desconto_total: e.target.value })} />
            </div>
            <div>
              <Label>Juros/multa extra (R$)</Label>
              <Input type="number" step="0.01" value={renegData.juros_extra_total} onChange={(e) => setRenegData({ ...renegData, juros_extra_total: e.target.value })} />
            </div>
            <div>
              <Label>Novas parcelas</Label>
              <Input type="number" min="1" value={renegData.novas_parcelas} onChange={(e) => setRenegData({ ...renegData, novas_parcelas: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Primeiro vencimento</Label>
              <Input type="date" value={renegData.primeiro_vencimento} onChange={(e) => setRenegData({ ...renegData, primeiro_vencimento: e.target.value })} />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" id="renegConfirm" className="w-4 h-4" checked={renegConfirm} onChange={(e) => setRenegConfirm(e.target.checked)} />
              <label htmlFor="renegConfirm" className="text-sm text-stone-600">Confirmo a renegociação</label>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setRenegDialogOpen(false)} className="flex-1">Cancelar</Button>
            <Button 
              variant="outline"
              onClick={async () => {
                const sel = parcelas.filter(p => selectedIds.includes(p.id) && p.status !== 'pago');
                const saldoPrincipal = sel.reduce((a, p) => a + (p.valor_principal || 0), 0);
                const taxa = (parseFloat(renegData.taxa_juros_percent) || 0) / 100;
                const meses = parseInt(renegData.novas_parcelas) || 1;
                const jurosTime = taxa * (meses / 12);
                const desconto = parseFloat(renegData.desconto_total) || 0;
                const extra = parseFloat(renegData.juros_extra_total) || 0;
                const totalReneg = Math.max(0, saldoPrincipal + (saldoPrincipal * jurosTime) + extra - desconto);
                const parcelaValor = totalReneg / meses;
                const primeiro = parseISO(renegData.primeiro_vencimento);
                const linhas = [];
                for (let i = 0; i < meses; i++) {
                  const venc = addMonths(primeiro, i);
                  linhas.push({
                    numero: i + 1,
                    vencimento: format(venc, 'yyyy-MM-dd'),
                    valor: parcelaValor
                  });
                }
                const headers = ['numero','vencimento','valor'];
                const csv = [headers.join(';')].concat(linhas.map(l => [l.numero, l.vencimento, l.valor].join(';'))).join('\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `comprovante_renegociacao_${financiamento.id}_${new Date().toISOString().split('T')[0]}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="flex-1"
            >
              Gerar Comprovante (CSV)
            </Button>
            <Button 
              onClick={async () => {
                if (!renegConfirm || selectedIds.length === 0) return;
                const perm = await agrogestao.functions.invoke('validarAcessoPropriedade', {
                  propriedade_id: financiamento.propriedade_id || localStorage.getItem('propriedade_selecionada'),
                  acao: 'financeiro_renegociacao'
                });
                if (!perm?.data?.success) { return; }
                const sel = parcelas.filter(p => selectedIds.includes(p.id) && p.status !== 'pago');
                const saldoPrincipal = sel.reduce((a, p) => a + (p.valor_principal || 0), 0);
                const taxa = (parseFloat(renegData.taxa_juros_percent) || 0) / 100;
                const meses = parseInt(renegData.novas_parcelas) || 1;
                const jurosTime = taxa * (meses / 12);
                const desconto = parseFloat(renegData.desconto_total) || 0;
                const extra = parseFloat(renegData.juros_extra_total) || 0;
                const totalReneg = Math.max(0, saldoPrincipal + (saldoPrincipal * jurosTime) + extra - desconto);
                const parcelaValor = totalReneg / meses;
                const primeiro = parseISO(renegData.primeiro_vencimento);
                try {
                  for (let i = 0; i < meses; i++) {
                    const venc = addMonths(primeiro, i);
                    await agrogestao.entities.ParcelaFinanciamento.create({
                      financiamento_id: financiamento.id,
                      numero_parcela: (financiamento.total_parcelas || 0) + 1 + i,
                      data_vencimento: format(venc, 'yyyy-MM-dd'),
                      valor_principal: parcelaValor * (saldoPrincipal / totalReneg),
                      valor_juros: parcelaValor - (parcelaValor * (saldoPrincipal / totalReneg)),
                      valor_total: parcelaValor,
                      status: 'pendente',
                      propriedade_id: financiamento.propriedade_id
                    });
                  }
                  // marcar selecionadas como renegociadas
                  for (const p of sel) {
                    await agrogestao.entities.ParcelaFinanciamento.update(p.id, { status: 'renegociado' });
                  }
                  const u = await agrogestao.auth.me();
                  await agrogestao.client.from('audit_logs').insert({
                    user_email: u?.email || null,
                    propriedade_id: financiamento.propriedade_id || localStorage.getItem('propriedade_selecionada') || null,
                    acao: 'renegociacao',
                    table: 'parcelas_financiamento',
                    status: 'success',
                    details: { financiamento_id: financiamento.id, parcelas_selecionadas: selectedIds, parametros: renegData, total_renegociado: totalReneg, novas_parcelas: meses }
                  });
                  setRenegDialogOpen(false);
                  setSelectedIds([]);
                  onUpdate();
                } catch {}
              }}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              disabled={!renegConfirm || selectedIds.length === 0}
            >
              Confirmar Renegociação
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={refundDialogOpen} onOpenChange={(v) => { setRefundDialogOpen(v); if (!v) setRefundParcela(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Estornar pagamento da parcela</DialogTitle>
            <DialogDescription>Esta operação é crítica. Preencha o motivo e confirme.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Motivo do estorno</Label>
              <Textarea rows={3} placeholder="Descreva o motivo" value={refundMotivo} onChange={(e) => setRefundMotivo(e.target.value)} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="refundConfirmParc" className="w-4 h-4" checked={refundConfirm} onChange={(e) => setRefundConfirm(e.target.checked)} />
              <label htmlFor="refundConfirmParc" className="text-sm text-stone-600">Confirmo o estorno deste pagamento</label>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setRefundDialogOpen(false); setRefundParcela(null); }} className="flex-1">Cancelar</Button>
            <Button 
              onClick={async () => {
                if (!refundConfirm || !refundParcela) return;
                try {
                  const perm = await agrogestao.functions.invoke('validarAcessoPropriedade', {
                    propriedade_id: refundParcela.propriedade_id || localStorage.getItem('propriedade_selecionada'),
                    acao: 'financeiro_estorno'
                  });
                  if (!perm?.data?.success) { return; }
                  const descPrefix = `Parcela ${refundParcela.numero_parcela}/${financiamento.total_parcelas}`;
                  const rels = await agrogestao.entities.Lancamento.filter({ tipo: 'despesa', data: refundParcela.data_pagamento }, null, 50);
                  const relacionado = (rels || []).find(l => (l.descricao || '').includes(descPrefix) && Math.abs((l.valor || 0) - (refundParcela.valor_pago || 0)) < 0.001);
                  if (relacionado) {
                    await agrogestao.entities.Lancamento.delete(relacionado.id);
                  }
                  await agrogestao.entities.ParcelaFinanciamento.update(refundParcela.id, { status: 'pendente', data_pagamento: null, valor_pago: null, multa_pago: 0, desconto_concedido: 0 });
                  const u = await agrogestao.auth.me();
                  await agrogestao.client.from('audit_logs').insert({
                    user_email: u?.email || null,
                    propriedade_id: refundParcela.propriedade_id || localStorage.getItem('propriedade_selecionada') || null,
                    acao: 'estorno_baixa',
                    table: 'parcelas_financiamento',
                    status: 'success',
                    details: { parcela_id: refundParcela.id, motivo: refundMotivo, original: { valor_total: refundParcela.valor_total, valor_pago: refundParcela.valor_pago, data_pagamento: refundParcela.data_pagamento } }
                  });
                  setRefundDialogOpen(false);
                  setRefundParcela(null);
                  onUpdate();
                } catch {}
              }}
              className="flex-1 bg-red-600 hover:bg-red-700"
              disabled={!refundConfirm || !refundMotivo}
            >
              Confirmar estorno
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
